import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, ChevronRight, Copy, Loader2, Radio, RefreshCw, Search, X } from 'lucide-react';
import { useLiveLogs, useLogList, useLogStatus } from '../hooks';
import type { LogEntry, LogQuery, LogSeverity } from '../api/logs';
import { copyText } from '../utils/clipboard';
import { getErrorDetail } from '../utils/error';

const RANGES = [15, 60, 360, 1440, 10080, 43200] as const;
const LEVELS: (LogSeverity | '')[] = ['', 'INFO', 'WARNING', 'ERROR'];
// 목록 한 줄에 메시지 옆으로 바로 보여 줄 필드. 나머지는 펼쳐서 본다.
const INLINE_FIELDS = ['error_code', 'status', 'path', 'tool', 'job'];

const kst = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  fractionalSecondDigits: 3,
  hour12: false,
});

const severityClass = (severity: string): string => {
  if (severity === 'ERROR' || severity === 'CRITICAL' || severity === 'ALERT' || severity === 'EMERGENCY')
    return 'bg-red-50 text-red-700 ring-red-200';
  if (severity === 'WARNING') return 'bg-amber-50 text-amber-800 ring-amber-200';
  if (severity === 'INFO' || severity === 'NOTICE') return 'bg-sky-50 text-sky-700 ring-sky-200';
  return 'bg-slate-50 text-slate-500 ring-slate-200';
};

const formatValue = (value: unknown): string => (typeof value === 'string' ? value : JSON.stringify(value));

interface TextFilters {
  service: string;
  q: string;
  request_id: string;
  user_id: string;
}

const EMPTY_TEXT: TextFilters = { service: '', q: '', request_id: '', user_id: '' };

const Logs: React.FC = () => {
  const { t } = useTranslation();
  const status = useLogStatus();
  const [sinceMinutes, setSinceMinutes] = useState<number>(60);
  // 조건을 바꾸거나 새로고침할 때만 '지금'을 다시 잰다 — 같은 조회의 다음 페이지는 같은 since 를 써야 한다.
  const [anchoredAt, setAnchoredAt] = useState(() => Date.now());
  const reanchor = () => setAnchoredAt(Date.now());
  const [minSeverity, setMinSeverity] = useState<LogSeverity | ''>('');
  const [draft, setDraft] = useState<TextFilters>(EMPTY_TEXT);
  const [applied, setApplied] = useState<TextFilters>(EMPTY_TEXT);
  const [live, setLive] = useState(false);
  const [hideHealth, setHideHealth] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const query: LogQuery = useMemo(
    () => ({
      since: new Date(anchoredAt - sinceMinutes * 60_000).toISOString(),
      min_severity: minSeverity || undefined,
      service: applied.service.trim(),
      q: applied.q.trim(),
      request_id: applied.request_id.trim(),
      user_id: applied.user_id.trim(),
      hide_health: hideHealth,
    }),
    [anchoredAt, sinceMinutes, minSeverity, applied, hideHealth],
  );

  const available = status.data?.available === true;
  const list = useLogList(query, available);
  const pagedEntries = useMemo(() => list.data?.pages.flatMap((p) => p.entries) ?? [], [list.data]);
  const liveLogs = useLiveLogs(query, pagedEntries[0]?.timestamp, live && available && list.isSuccess);
  const entries = useMemo(() => [...liveLogs.entries, ...pagedEntries], [liveLogs.entries, pagedEntries]);
  const knownServices = useMemo(() => [...new Set(entries.map((e) => e.service))].sort(), [entries]);

  const apply = (next: TextFilters) => {
    setDraft(next);
    setApplied(next);
    setExpanded(null);
    reanchor();
  };

  const narrowTo = (key: 'request_id' | 'user_id', value: string) => apply({ ...EMPTY_TEXT, [key]: value });

  const rowKey = (entry: LogEntry, index: number) => entry.insert_id ?? `${entry.timestamp}-${index}`;

  if (status.isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-slate-400" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('logs.title')}</h2>
          <p className="text-sm text-slate-500 mt-1">{t('logs.subtitle', { env: status.data?.env ?? '-' })}</p>
        </div>
        {available && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setLive((v) => !v)}
              aria-pressed={live}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                live
                  ? 'bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Radio size={16} className={live ? 'motion-safe:animate-pulse' : ''} />
              {live ? t('logs.liveOn') : t('logs.liveOff')}
            </button>
            <button
              type="button"
              onClick={reanchor}
              disabled={list.isFetching}
              className="btn-secondary inline-flex items-center gap-2 text-sm"
            >
              <RefreshCw size={16} className={list.isFetching ? 'animate-spin' : ''} />
              {t('logs.refresh')}
            </button>
          </div>
        )}
      </div>

      {!available ? (
        <div className="card p-6 flex gap-4">
          <AlertTriangle className="text-amber-500 shrink-0" size={24} />
          <div className="space-y-2 text-sm">
            <p className="font-semibold text-slate-800">{t('logs.unavailableTitle')}</p>
            <p className="text-slate-600">
              {status.data?.reason ?? getErrorDetail(status.error, t('logs.unavailableTitle'))}
            </p>
            <p className="text-slate-500">{t('logs.unavailableHint')}</p>
          </div>
        </div>
      ) : (
        <>
          <form
            className="card p-4 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              apply(draft);
            }}
          >
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                {t('logs.range')}
                <select
                  className="input w-auto py-1.5"
                  value={sinceMinutes}
                  onChange={(e) => {
                    setSinceMinutes(Number(e.target.value));
                    reanchor();
                  }}
                >
                  {RANGES.map((m) => (
                    <option key={m} value={m}>
                      {t(`logs.ranges.${m}`)}
                    </option>
                  ))}
                </select>
              </label>
              <div
                role="group"
                aria-label={t('logs.level')}
                className="inline-flex rounded-lg border border-slate-200 p-0.5"
              >
                {LEVELS.map((level) => (
                  <button
                    key={level || 'all'}
                    type="button"
                    aria-pressed={minSeverity === level}
                    onClick={() => {
                      setMinSeverity(level);
                      reanchor();
                    }}
                    className={`px-3 py-1 rounded-md text-sm transition-colors ${
                      minSeverity === level ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {t(`logs.levels.${level || 'ALL'}`)}
                  </button>
                ))}
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  className="rounded border-slate-300"
                  checked={hideHealth}
                  onChange={(e) => {
                    setHideHealth(e.target.checked);
                    reanchor();
                  }}
                />
                {t('logs.hideHealth')}
              </label>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[10rem_1fr_14rem_14rem_auto]">
              <input
                className="input"
                list="log-services"
                placeholder={t('logs.service')}
                aria-label={t('logs.service')}
                value={draft.service}
                onChange={(e) => setDraft({ ...draft, service: e.target.value })}
              />
              <datalist id="log-services">
                {knownServices.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
              <input
                className="input"
                placeholder={t('logs.searchPlaceholder')}
                aria-label={t('logs.search')}
                value={draft.q}
                onChange={(e) => setDraft({ ...draft, q: e.target.value })}
              />
              <input
                className="input font-mono"
                placeholder="request_id"
                aria-label="request_id"
                value={draft.request_id}
                onChange={(e) => setDraft({ ...draft, request_id: e.target.value })}
              />
              <input
                className="input font-mono"
                placeholder="user_id"
                aria-label="user_id"
                value={draft.user_id}
                onChange={(e) => setDraft({ ...draft, user_id: e.target.value })}
              />
              <div className="flex gap-2">
                <button type="submit" className="btn-primary inline-flex items-center gap-2 text-sm">
                  <Search size={16} />
                  {t('logs.search')}
                </button>
                {Object.values(applied).some(Boolean) && (
                  <button
                    type="button"
                    onClick={() => apply(EMPTY_TEXT)}
                    className="btn-secondary inline-flex items-center gap-1 text-sm"
                  >
                    <X size={14} />
                    {t('logs.clear')}
                  </button>
                )}
              </div>
            </div>
          </form>

          {(list.error || liveLogs.error) && (
            <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              {getErrorDetail(list.error ?? liveLogs.error, t('logs.loadFailed'))}
            </p>
          )}

          <div className="card overflow-hidden">
            {list.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="animate-spin text-slate-400" size={24} />
              </div>
            ) : entries.length === 0 ? (
              <p className="py-16 text-center text-sm text-slate-500">{t('logs.empty')}</p>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm">
                {entries.map((entry, i) => {
                  const key = rowKey(entry, i);
                  const isOpen = expanded === key;
                  const inline = INLINE_FIELDS.filter((f) => entry.fields[f] !== undefined);
                  const requestId = entry.fields.request_id;
                  const userId = entry.fields.user_id;
                  return (
                    <li key={key} className={isOpen ? 'bg-slate-50' : undefined}>
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : key)}
                        aria-expanded={isOpen}
                        className="w-full grid grid-cols-[auto_8.5rem_5.5rem_7rem_1fr] items-start gap-3 px-4 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                      >
                        {isOpen ? (
                          <ChevronDown size={14} className="mt-1 text-slate-400" />
                        ) : (
                          <ChevronRight size={14} className="mt-1 text-slate-400" />
                        )}
                        <time dateTime={entry.timestamp} className="tabular-nums text-slate-500 whitespace-nowrap">
                          {kst.format(new Date(entry.timestamp))}
                        </time>
                        <span
                          className={`justify-self-start rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${severityClass(entry.severity)}`}
                        >
                          {entry.severity}
                        </span>
                        <span className="truncate text-slate-600">{entry.service}</span>
                        <span className="min-w-0">
                          <span className="font-mono text-slate-800 break-words">{entry.message}</span>
                          {inline.map((f) => (
                            <span key={f} className="ml-2 font-mono text-xs text-slate-500">
                              {f}=<span className="text-slate-700">{formatValue(entry.fields[f])}</span>
                            </span>
                          ))}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 pl-11 space-y-3">
                          {Object.keys(entry.fields).length > 0 && (
                            <dl className="grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-4 gap-y-1 font-mono text-xs">
                              {Object.entries(entry.fields).map(([k, v]) => (
                                <React.Fragment key={k}>
                                  <dt className="text-slate-500">{k}</dt>
                                  <dd className="text-slate-800 break-all whitespace-pre-wrap">{formatValue(v)}</dd>
                                </React.Fragment>
                              ))}
                            </dl>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {typeof requestId === 'string' && requestId !== '' && (
                              <button
                                type="button"
                                className="btn-secondary text-xs"
                                onClick={() => narrowTo('request_id', requestId)}
                              >
                                {t('logs.sameRequest')}
                              </button>
                            )}
                            {typeof userId === 'string' && userId !== '' && (
                              <button
                                type="button"
                                className="btn-secondary text-xs"
                                onClick={() => narrowTo('user_id', userId)}
                              >
                                {t('logs.sameUser')}
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn-secondary text-xs inline-flex items-center gap-1"
                              onClick={() => copyText(JSON.stringify(entry, null, 2))}
                            >
                              <Copy size={12} />
                              {t('logs.copyJson')}
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {list.hasNextPage && (
            <div className="flex justify-center">
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-2 text-sm"
                onClick={() => list.fetchNextPage()}
                disabled={list.isFetchingNextPage}
              >
                {list.isFetchingNextPage && <Loader2 size={14} className="animate-spin" />}
                {t('logs.loadOlder')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Logs;
